import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { Globe, Compass, Sparkles, Map, DollarSign, Menu, X, Heart } from "lucide-react";
import { useState } from "react";
import { useTrip } from "../TripContext";
import { useFavorites } from "../FavoritesContext";

export default function Header() {
  const { t, lang, toggleLang, nextLangLabel } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { stops } = useTrip();
  const { ids: favIds } = useFavorites();

  const nav = [
    { to: "/", label: t("nav_explore"), icon: Compass },
    { to: "/prices", label: t("nav_prices"), icon: DollarSign },
    { to: "/planner", label: t("nav_planner"), icon: Map, badge: stops.length || null },
    { to: "/favorites", label: lang === "ar" ? "المفضلة" : lang === "fr" ? "Favoris" : "Favorites", icon: Heart, badge: favIds.length || null },
    { to: "/assistant", label: t("nav_assistant"), icon: Sparkles },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF7F2]/85 border-b border-[#E6DED3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <div className="h-9 w-9 rounded-xl bg-[#1C1917] text-[#FAF7F2] grid place-items-center font-display text-lg">
            ر
          </div>
          <div className="leading-none">
            <div className="font-display text-xl text-[#1C1917]">{t("brand")}</div>
            <div className="text-[10px] text-[#78716C] uppercase tracking-widest">Middle East · MENA</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "") || "home"}`}
                className={`relative px-4 py-2 rounded-full text-sm transition-colors flex items-center gap-2 ${
                  active
                    ? "bg-[#1C1917] text-[#FAF7F2]"
                    : "text-[#57534E] hover:bg-[#F3EDE4]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
                {item.badge ? (
                  <span className="ms-1 min-w-[20px] h-5 px-1 grid place-items-center bg-[#C85A32] text-[#FAF7F2] rounded-full text-[11px] font-semibold">
                    {item.badge}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="h-9 px-3 rounded-full border border-[#E6DED3] hover:border-[#1C1917] text-sm flex items-center gap-1.5 transition-colors"
          >
            <Globe className="h-4 w-4" />
            {nextLangLabel}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden h-9 w-9 grid place-items-center rounded-full border border-[#E6DED3]"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E6DED3] bg-[#FAF7F2]">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {nav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl ${
                    active ? "bg-[#1C1917] text-[#FAF7F2]" : "text-[#1C1917]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                  {item.badge ? (
                    <span className="ms-auto min-w-[20px] h-5 px-1 grid place-items-center bg-[#C85A32] text-[#FAF7F2] rounded-full text-[11px]">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
